import type { Route } from '@metro/routing'

/**
 * The slice of a schema connection the platform-sign index reads — a
 * byte-identical subset of the host's `SchemaConnection`, inlined so this
 * package stays self-contained (same pattern as
 * `SchemaConvenientTransferCars`).
 */
export interface PlatformBoundConnection {
  readonly lineId: string
  readonly fromStationId?: string
  readonly toStationId?: string
  readonly reversible?: boolean
  /** Sign when travelling from → to ("Westbound", …), schema ≥ 1.30.0. */
  readonly forwardBound?: string
  /** Sign when travelling to → from; only read on reversible connections. */
  readonly reverseBound?: string
}

/** (line, boarding station, next station) → platform sign, or undefined. */
export type PlatformBoundLookup = (
  lineId: string,
  fromStationId: string,
  toStationId: string,
) => string | undefined

/**
 * Indexes every connection's platform sign per travel direction, so a
 * route build is one map lookup per ride leg. Empty / missing signs are
 * not indexed — the row simply does not render. Cross-platform parity:
 * iOS `RouteDetailsPlatformBoundResolver`, Android `PlatformBoundIndex`.
 */
export function platformBoundLookup(
  connections: readonly PlatformBoundConnection[],
): PlatformBoundLookup {
  const signs = new Map<string, string>()
  const key = (lineId: string, from: string, to: string) => `${lineId}|${from}|${to}`
  for (const c of connections) {
    if (c.fromStationId === undefined || c.toStationId === undefined) continue
    if (c.forwardBound) signs.set(key(c.lineId, c.fromStationId, c.toStationId), c.forwardBound)
    if (c.reversible === true && c.reverseBound) {
      signs.set(key(c.lineId, c.toStationId, c.fromStationId), c.reverseBound)
    }
  }
  return (lineId, from, to) => signs.get(key(lineId, from, to))
}

/**
 * The platform sign ("Westbound", …) for each ride leg, keyed by its index
 * in `route.legs` (the same key `possibleTransfersByLeg` uses). Read from
 * the leg's first two WIRE stops, before any display dedup: the train is
 * boarded at the first stop even when a branch-junction duplicate hides
 * it. Shown verbatim in every locale — the rider matches it against the
 * station wall.
 */
export function buildPlatformBoundsByLeg(
  route: Route,
  lookup: PlatformBoundLookup,
): Record<number, string> {
  const out: Record<number, string> = {}
  route.legs.forEach((leg, index) => {
    if (leg.type !== 'ride' || leg.line_id === undefined) return
    const from = leg.stops?.[0]?.stationId
    const to = leg.stops?.[1]?.stationId
    if (from === undefined || to === undefined) return
    const sign = lookup(leg.line_id, from, to)
    if (sign) out[index] = sign
  })
  return out
}
